import React, { useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { TabPanel } from 'primereact/tabview'

import {
  Btn,
  CustomCheckbox,
  CustomDataTableHeader,
  CustomModal,
  CustomMultiSelect,
  CustomRadioButton,
  CustomTabView,
  Loading,
  Periodo,
} from '../../../../components'
import { tableCellCentered } from '../../../../assets/js/styles'
import { showMessageBox } from '../../../../store/actions/system.actions'
import { parseToMysqlFormat } from '../../../../services/util/date.service'
import PainelMonitoramentoEntregasHttp from '../../../../services/api/https/dashboard/painel-monitoramento-entregas.http'

const ROTAS_OPTIONS = [
  { value: 'em_andamento', label: 'Apenas Rotas em Andamento no período' },
  { value: 'todas', label: 'Todas as Rotas no período' },
]

const STATUS_OPTIONS = [
  { label: 'Em Rota', value: 'Em Rota' },
  { label: 'Parado', value: 'Parado' },
]

function getPeriodoHoje() {
  const hoje = new Date()
  return {
    dataInicial: new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate(), 0, 0, 0),
    dataFinal: new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate(), 23, 59, 59),
  }
}

function buildAnalistaOptions(rows) {
  const byId = new Map()
  rows.forEach(row =>
    byId.set(row.analista_entrega_id, {
      analista_entrega_id: row.analista_entrega_id,
      analista_name: row.analista_name,
    }),
  )
  return Array.from(byId.values()).sort((a, b) => a.analista_name.localeCompare(b.analista_name))
}

function buildTpOptions(rows) {
  const byId = new Map()
  rows.forEach(row => byId.set(row.tp_id, { tp_id: row.tp_id, tp_name: row.tp_name }))
  return Array.from(byId.values()).sort((a, b) => a.tp_name.localeCompare(b.tp_name))
}

function ModalAssumirRotas({ visible, onClose, analistaOrigem, onSuccess, dispatch }) {
  const [activeIndex, setActiveIndex] = useState(0)
  const [maximized, setMaximized] = useState(false)

  useEffect(() => {
    if (visible) {
      setActiveIndex(0)
      setMaximized(false)
    }
  }, [visible])

  return (
    <CustomModal
      header="Assumir Rotas/Veículos"
      isVisible={visible}
      onClose={onClose}
      maximizable
      maximized={maximized}
      onMaximize={e => setMaximized(e.maximized)}
      width={maximized ? '100vw' : 'min(94vw, 1200px)'}
      style={maximized ? { height: '100vh' } : undefined}
      contentStyle={{ overflowY: 'auto', maxHeight: maximized ? 'calc(100vh - 110px)' : '80vh' }}
    >
      <p style={{ marginBottom: 12 }}>
        <strong>Analista de Origem:</strong> {analistaOrigem ? analistaOrigem.analista_name : '-'}
      </p>

      {visible && analistaOrigem && (
        <CustomTabView activeIndex={activeIndex} onTabChange={setActiveIndex}>
          <TabPanel header="Rotas">
            <AssumirRotasTab
              analistaOrigem={analistaOrigem}
              dispatch={dispatch}
              onSuccess={onSuccess}
            />
          </TabPanel>
          <TabPanel header="Veículos">
            <AssumirVeiculosTab
              analistaOrigem={analistaOrigem}
              dispatch={dispatch}
              onSuccess={onSuccess}
            />
          </TabPanel>
        </CustomTabView>
      )}
    </CustomModal>
  )
}

function AssumirRotasTab({ analistaOrigem, dispatch, onSuccess }) {
  const periodoInicial = useMemo(() => getPeriodoHoje(), [])
  const [pInicial, setPInicial] = useState(periodoInicial.dataInicial)
  const [pFinal, setPFinal] = useState(periodoInicial.dataFinal)
  const [rotasOption, setRotasOption] = useState('em_andamento')
  const [rotas, setRotas] = useState([])
  const [tpFilter, setTpFilter] = useState([])
  const [analistaFilter, setAnalistaFilter] = useState([])
  const [globalFilter, setGlobalFilter] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    fetchRotas()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchRotas = async () => {
    if (!pInicial || !pFinal) {
      dispatch(showMessageBox(null, 'Informe o Período Inicial e o Período Final'))
      return
    }

    try {
      setLoading(true)
      const data = await PainelMonitoramentoEntregasHttp.getAssumirRotas(
        analistaOrigem.analista_entrega_id,
        rotasOption,
        parseToMysqlFormat(pInicial),
        parseToMysqlFormat(pFinal),
      )

      setRotas(
        (data.rotas || []).map(rota => ({
          ...rota,
          selected: false,
          assumir: false,
          assumir_label: '',
        })),
      )
      setTpFilter([])
      setAnalistaFilter([])
    } catch (error) {
      setRotas([])
      dispatch(showMessageBox(null, error))
    } finally {
      setLoading(false)
    }
  }

  const tpOptions = useMemo(() => buildTpOptions(rotas), [rotas])
  const analistaOptions = useMemo(() => buildAnalistaOptions(rotas), [rotas])

  const rotasVisiveis = useMemo(() => {
    let visiveis = rotas
    if (tpFilter.length) {
      const tpIds = new Set(tpFilter.map(tp => tp.tp_id))
      visiveis = visiveis.filter(rota => tpIds.has(rota.tp_id))
    }
    if (analistaFilter.length) {
      const analistaIds = new Set(analistaFilter.map(analista => analista.analista_entrega_id))
      visiveis = visiveis.filter(rota => analistaIds.has(rota.analista_entrega_id))
    }
    return visiveis
  }, [rotas, tpFilter, analistaFilter])

  const todasSelecionadas =
    rotasVisiveis.length > 0 && rotasVisiveis.every(rota => rota.selected)
  const temAssumir = rotas.some(rota => rota.assumir)

  const toggleSelecionarTodos = checked => {
    const visiveisIds = new Set(rotasVisiveis.map(rota => rota.fleet_id))
    setRotas(prev =>
      prev.map(rota => (visiveisIds.has(rota.fleet_id) ? { ...rota, selected: checked } : rota)),
    )
  }

  const toggleSelecionado = (rota, checked) => {
    setRotas(prev =>
      prev.map(item => (item.fleet_id === rota.fleet_id ? { ...item, selected: checked } : item)),
    )
  }

  const assumir = () => {
    const selecionadas = rotas.filter(rota => rota.selected)
    if (selecionadas.length === 0) {
      dispatch(showMessageBox(null, 'Selecione pelo menos uma rota na tabela.'))
      return
    }

    setRotas(prev =>
      prev.map(rota =>
        rota.selected ? { ...rota, assumir: true, assumir_label: 'Sim', selected: false } : rota,
      ),
    )
  }

  const salvar = async () => {
    const fleetIds = rotas.filter(rota => rota.assumir).map(rota => rota.fleet_id)

    if (fleetIds.length === 0) {
      dispatch(showMessageBox(null, 'Marque pelo menos uma rota para assumir antes de Salvar'))
      return
    }

    try {
      setSaving(true)
      const result = await PainelMonitoramentoEntregasHttp.salvarAssumirRotas(
        analistaOrigem.analista_entrega_id,
        fleetIds,
      )
      dispatch(showMessageBox(`${result.qte_rotas_assumidas} rota(s) assumida(s) com sucesso!`))
      await fetchRotas()
      if (onSuccess) onSuccess()
    } catch (error) {
      dispatch(showMessageBox(null, error))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{ padding: '12px 4px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: '0.8rem', color: '#14b0bf', marginBottom: 2 }}>
            Data Prevista da Rota
          </div>
          <Periodo
            periodoInicial={pInicial}
            periodoFinal={pFinal}
            showButton={false}
            onChangePeriodoInicial={setPInicial}
            onChangePeriodoFinal={setPFinal}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingBottom: 4 }}>
          {ROTAS_OPTIONS.map(option => (
            <CustomRadioButton
              key={option.value}
              items={[option]}
              value={rotasOption}
              onChange={setRotasOption}
            />
          ))}
        </div>
        <div style={{ paddingBottom: 4 }}>
          <Btn onClick={fetchRotas} disabled={loading}>
            Buscar Rotas
          </Btn>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 240 }}>
          <CustomMultiSelect
            label="Filtrar por TP"
            optionLabel="tp_name"
            options={tpOptions}
            value={tpFilter}
            onChange={setTpFilter}
            appendTo={document.body}
          />
        </div>
        <div style={{ minWidth: 260 }}>
          <CustomMultiSelect
            label="Filtrar por Analista"
            optionLabel="analista_name"
            options={analistaOptions}
            value={analistaFilter}
            onChange={setAnalistaFilter}
            appendTo={document.body}
          />
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '14px 0 10px 0' }}>
        <CustomCheckbox checked={todasSelecionadas} onChange={toggleSelecionarTodos}>
          Selecionar Todos
        </CustomCheckbox>
        <Btn onClick={assumir} disabled={loading || saving}>
          Assumir
        </Btn>
        <Btn onClick={salvar} disabled={loading || !temAssumir} isLoading={saving}>
          Salvar
        </Btn>
      </div>

      {loading ? (
        <Loading size="md" />
      ) : (
        <DataTable
          header={<CustomDataTableHeader onChange={setGlobalFilter} />}
          globalFilter={globalFilter}
          value={rotasVisiveis}
          paginator
          rows={10}
          emptyMessage="Nenhuma rota de outros Analistas encontrada no período selecionado"
        >
          <Column
            header=""
            style={tableCellCentered(50)}
            body={rota => (
              <CustomCheckbox
                checked={rota.selected === true}
                disabled={saving}
                onChange={checked => toggleSelecionado(rota, checked)}
              />
            )}
          />
          <Column header="Veículo" field="vehicle_badge" style={tableCellCentered()} />
          <Column header="Motorista" field="driver_name" style={tableCellCentered()} />
          <Column header="Data Prevista" field="expected_date" style={tableCellCentered()} />
          <Column header="TP" field="tp_name" style={tableCellCentered()} />
          <Column header="Analista" field="analista_name" style={tableCellCentered()} />
          <Column header="Assumir" field="assumir_label" style={tableCellCentered(90)} />
        </DataTable>
      )}
    </div>
  )
}

function AssumirVeiculosTab({ analistaOrigem, dispatch, onSuccess }) {
  const [veiculos, setVeiculos] = useState([])
  const [tpFilter, setTpFilter] = useState([])
  const [analistaFilter, setAnalistaFilter] = useState([])
  const [statusFilter, setStatusFilter] = useState([])
  const [globalFilter, setGlobalFilter] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    fetchVeiculos()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchVeiculos = async () => {
    try {
      setLoading(true)
      const data = await PainelMonitoramentoEntregasHttp.getAssumirVeiculos(
        analistaOrigem.analista_entrega_id,
      )
      setVeiculos(
        (data.veiculos || []).map(veiculo => ({
          ...veiculo,
          selected: false,
          assumir: false,
          assumir_label: '',
        })),
      )
      setTpFilter([])
      setAnalistaFilter([])
      setStatusFilter([])
    } catch (error) {
      setVeiculos([])
      dispatch(showMessageBox(null, error))
    } finally {
      setLoading(false)
    }
  }

  const tpOptions = useMemo(() => buildTpOptions(veiculos), [veiculos])
  const analistaOptions = useMemo(() => buildAnalistaOptions(veiculos), [veiculos])

  const veiculosVisiveis = useMemo(() => {
    let visiveis = veiculos
    if (tpFilter.length) {
      const tpIds = new Set(tpFilter.map(tp => tp.tp_id))
      visiveis = visiveis.filter(veiculo => tpIds.has(veiculo.tp_id))
    }
    if (analistaFilter.length) {
      const analistaIds = new Set(analistaFilter.map(analista => analista.analista_entrega_id))
      visiveis = visiveis.filter(veiculo => analistaIds.has(veiculo.analista_entrega_id))
    }
    if (statusFilter.length) {
      visiveis = visiveis.filter(veiculo => statusFilter.includes(veiculo.status))
    }
    return visiveis
  }, [veiculos, tpFilter, analistaFilter, statusFilter])

  const todasSelecionadas =
    veiculosVisiveis.length > 0 && veiculosVisiveis.every(veiculo => veiculo.selected)
  const temAssumir = veiculos.some(veiculo => veiculo.assumir)

  const toggleSelecionarTodos = checked => {
    const visiveisIds = new Set(veiculosVisiveis.map(veiculo => veiculo.vehicle_id))
    setVeiculos(prev =>
      prev.map(veiculo =>
        visiveisIds.has(veiculo.vehicle_id) ? { ...veiculo, selected: checked } : veiculo,
      ),
    )
  }

  const toggleSelecionado = (veiculo, checked) => {
    setVeiculos(prev =>
      prev.map(item =>
        item.vehicle_id === veiculo.vehicle_id ? { ...item, selected: checked } : item,
      ),
    )
  }

  const assumir = () => {
    const selecionados = veiculos.filter(veiculo => veiculo.selected)
    if (selecionados.length === 0) {
      dispatch(showMessageBox(null, 'Selecione pelo menos um veículo na tabela.'))
      return
    }

    setVeiculos(prev =>
      prev.map(veiculo =>
        veiculo.selected
          ? { ...veiculo, assumir: true, assumir_label: 'Sim', selected: false }
          : veiculo,
      ),
    )
  }

  const salvar = async () => {
    const vehicleIds = veiculos.filter(veiculo => veiculo.assumir).map(veiculo => veiculo.vehicle_id)

    if (vehicleIds.length === 0) {
      dispatch(showMessageBox(null, 'Marque pelo menos um veículo para assumir antes de Salvar'))
      return
    }

    try {
      setSaving(true)
      const result = await PainelMonitoramentoEntregasHttp.salvarAssumirVeiculos(
        analistaOrigem.analista_entrega_id,
        vehicleIds,
      )
      dispatch(
        showMessageBox(`${result.qte_veiculos_assumidos} veículo(s) assumido(s) com sucesso!`),
      )
      await fetchVeiculos()
      if (onSuccess) onSuccess()
    } catch (error) {
      dispatch(showMessageBox(null, error))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{ padding: '12px 4px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 240 }}>
          <CustomMultiSelect
            label="Filtrar por TP"
            optionLabel="tp_name"
            options={tpOptions}
            value={tpFilter}
            onChange={setTpFilter}
            appendTo={document.body}
          />
        </div>
        <div style={{ minWidth: 260 }}>
          <CustomMultiSelect
            label="Filtrar por Analista"
            optionLabel="analista_name"
            options={analistaOptions}
            value={analistaFilter}
            onChange={setAnalistaFilter}
            appendTo={document.body}
          />
        </div>
        <div style={{ minWidth: 220 }}>
          <CustomMultiSelect
            label="Filtrar por Status"
            options={STATUS_OPTIONS}
            value={statusFilter}
            onChange={setStatusFilter}
            filter={false}
            appendTo={document.body}
          />
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '14px 0 10px 0' }}>
        <CustomCheckbox checked={todasSelecionadas} onChange={toggleSelecionarTodos}>
          Selecionar Todos
        </CustomCheckbox>
        <Btn onClick={assumir} disabled={loading || saving}>
          Assumir
        </Btn>
        <Btn onClick={salvar} disabled={loading || !temAssumir} isLoading={saving}>
          Salvar
        </Btn>
      </div>

      {loading ? (
        <Loading size="md" />
      ) : (
        <DataTable
          header={<CustomDataTableHeader onChange={setGlobalFilter} />}
          globalFilter={globalFilter}
          value={veiculosVisiveis}
          paginator
          rows={10}
          emptyMessage="Nenhum veículo de outros Analistas encontrado nos TPs do Analista de origem"
        >
          <Column
            header=""
            style={tableCellCentered(50)}
            body={veiculo => (
              <CustomCheckbox
                checked={veiculo.selected === true}
                disabled={saving}
                onChange={checked => toggleSelecionado(veiculo, checked)}
              />
            )}
          />
          <Column header="Placa" field="vehicle_badge" style={tableCellCentered()} />
          <Column header="Capacidade KG" field="capacity_kg" style={tableCellCentered()} />
          <Column header="Status" field="status" style={tableCellCentered()} />
          <Column header="TP" field="tp_name" style={tableCellCentered()} />
          <Column header="Analista" field="analista_name" style={tableCellCentered()} />
          <Column header="Assumir" field="assumir_label" style={tableCellCentered(90)} />
        </DataTable>
      )}
    </div>
  )
}

export default connect()(ModalAssumirRotas)
